export default function SkillProfile() {
  const recentBadges = [
    { name: 'AI Foundations', color: 'bg-gradient-to-br from-blue-400 to-blue-600' },
    { name: 'Creative Thinker', color: 'bg-gradient-to-br from-purple-400 to-purple-600' },
    { name: 'Growth Starter', color: 'bg-gradient-to-br from-pink-400 to-pink-600' },
    { name: 'Problem Solver', color: 'bg-gradient-to-br from-green-400 to-green-600' },
    { name: 'Presentation Pro', color: 'bg-gradient-to-br from-orange-400 to-orange-600' },
  ];

  const completedSkills = [
    'AI Productivity Boost',
    'Mastering AI Prompts',
    'Design an Impactful Presentation',
    'Find Your Career Theme',
    'Business Models in 45 Min',
    'Creative Thinking Micro-Exercises',
  ];

  const features = [
    {
      icon: 'ri-medal-line',
      title: 'Stand Out',
      description: 'Show employers and universities what you\'ve actually learned.',
      gradient: 'from-[#304FFE] to-[#5B9FED]',
    },
    {
      icon: 'ri-share-line',
      title: 'Share Everywhere',
      description: 'Add your profile to LinkedIn, resumes and applications instantly.',
      gradient: 'from-[#7C4DFF] to-[#B794F6]',
    },
    {
      icon: 'ri-line-chart-line',
      title: 'Track Your Growth',
      description: 'See your progress over time and celebrate every milestone.',
      gradient: 'from-[#00BFA5] to-[#00E676]',
    },
  ];

  return (
    <section className="py-28 px-6 lg:px-12 relative overflow-hidden">
      {/* Animated gradient background - same as Hero */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#304FFE]/[0.08] via-transparent to-[#7C4DFF]/[0.08]">
        <div className="absolute inset-0 bg-gradient-to-b from-black/5 via-transparent to-transparent"></div>
      </div>

      {/* Floating decorative elements - same as Hero */}
      <div className="absolute top-1/4 right-[10%] w-64 h-64 bg-gradient-to-br from-[#304FFE]/20 to-[#7C4DFF]/20 rounded-full blur-3xl animate-pulse"></div>
      <div className="absolute bottom-1/4 left-[15%] w-48 h-48 bg-gradient-to-br from-[#7C4DFF]/15 to-[#304FFE]/15 rounded-full blur-3xl animate-pulse delay-1000"></div>

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Header */}
        <div className="text-center mb-20">
          <h2 className="text-5xl lg:text-6xl font-bold text-[#1A1F36] mb-6">
            Build a skill profile that speaks for you.
          </h2>
          <p className="text-2xl text-gray-600 max-w-3xl mx-auto font-light">
            Your achievements, all in one place. Share them anywhere.
          </p>
        </div>

        {/* Two Column Layout */}
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left: Skill Profile Card */}
          <div className="bg-gradient-to-br from-[#1A1F36] to-[#2D3561] rounded-3xl p-10 shadow-lg border border-gray-800 relative overflow-hidden group">
            {/* Decorative glow */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-purple-500 rounded-full blur-3xl opacity-20 group-hover:opacity-30 transition-opacity duration-700"></div>
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-blue-500 rounded-full blur-3xl opacity-20 group-hover:opacity-30 transition-opacity duration-700"></div>

            <div className="relative z-10">
              {/* Header */}
              <div className="flex items-center gap-4 mb-8 pb-6 border-b border-gray-700">
                {/* Avatar */}
                <div className="w-16 h-16 bg-gradient-to-br from-purple-400 to-blue-500 rounded-full flex items-center justify-center text-2xl font-bold text-white shadow-lg">
                  AS
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-white">Alex Smith</h3>
                  <p className="text-sm text-gray-400">Praxia Learner</p>
                </div>
              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-3 gap-4 mb-8">
                <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10">
                  <div className="text-3xl font-bold text-white mb-1">12</div>
                  <div className="text-xs text-gray-400">Skills Completed</div>
                </div>
                <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10">
                  <div className="text-3xl font-bold text-white mb-1">18</div>
                  <div className="text-xs text-gray-400">Badges Earned</div>
                </div>
                <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10">
                  <div className="text-3xl font-bold text-white mb-1">9.5</div>
                  <div className="text-xs text-gray-400">Hours Learned</div>
                </div>
              </div>

              {/* Recent Badges */}
              <div className="mb-8">
                <h4 className="text-sm font-semibold text-gray-400 mb-4">RECENT BADGES</h4>
                <div className="grid grid-cols-3 gap-3">
                  {recentBadges.map((badge, index) => (
                    <div
                      key={index}
                      className="relative group/badge"
                    >
                      <div
                        className={`${badge.color} rounded-xl p-4 shadow-lg hover:scale-105 transition-transform duration-300 cursor-pointer flex flex-col justify-between min-h-[100px]`}
                      >
                        <div className="w-8 h-8 flex items-center justify-center">
                          <i className="ri-medal-fill text-2xl text-white group-hover/badge:scale-110 transition-transform"></i>
                        </div>
                        <div className="text-xs text-white font-medium leading-tight mt-2">
                          {badge.name}
                        </div>
                      </div>
                    </div>
                  ))}
                  {/* More badges indicator */}
                  <div className="relative group/badge">
                    <div className="bg-gradient-to-br from-indigo-400 to-indigo-600 rounded-xl p-4 shadow-lg hover:scale-105 transition-transform duration-300 cursor-pointer min-h-[100px] flex items-center justify-center">
                      <span className="text-lg font-bold text-white">+11</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Completed Skills */}
              <div className="mb-8">
                <h4 className="text-sm font-semibold text-gray-400 mb-4">COMPLETED SKILLS</h4>
                <div className="space-y-2">
                  {completedSkills.slice(0, 4).map((skill, index) => (
                    <div key={index} className="flex items-center gap-3 text-sm text-gray-300">
                      <div className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                        <i className="ri-check-line text-xs text-white"></i>
                      </div>
                      <span>{skill}</span>
                    </div>
                  ))}
                  <div className="text-sm text-purple-400 font-semibold cursor-pointer hover:text-purple-300 transition-colors mt-3">
                    + {completedSkills.length - 4} more skills
                  </div>
                </div>
              </div>

              {/* Recommended Next Steps */}
              <div className="bg-gradient-to-r from-purple-500/20 to-blue-500/20 rounded-xl p-5 border border-purple-500/30">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-purple-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-lightbulb-flash-line text-white"></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-white mb-1">Recommended Next</h4>
                    <p className="text-xs text-gray-300">AI for Smarter Research</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right: Feature Cards */}
          <div className="space-y-6">
            {features.map((feature, index) => (
              <div
                key={index}
                className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 border border-gray-100 relative overflow-hidden group"
              >
                {/* Subtle glow */}
                <div className={`absolute inset-0 bg-gradient-to-br ${feature.gradient} opacity-0 group-hover:opacity-5 transition-opacity duration-500`}></div>

                <div className="relative z-10 flex items-start gap-6">
                  {/* Icon */}
                  <div className={`w-14 h-14 bg-gradient-to-br ${feature.gradient} rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 shadow-lg`}>
                    <i className={`${feature.icon} text-2xl text-white`}></i>
                  </div>

                  {/* Content */}
                  <div>
                    <h4 className="text-2xl font-bold text-[#1A1F36] mb-3">
                      {feature.title}
                    </h4>
                    <p className="text-base text-gray-600 leading-relaxed">
                      {feature.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
